import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AgentRemindersToast — polls /api/jarvis/agent_reminders every 60s and shows
 * a SILENT visual nudge. The agent does NOT speak unprompted any more —
 * owner complaint: "Sarah keeps trying to talk back to me first when I
 * didn't even say her name". Now the owner taps 🔊 to hear it on demand,
 * or just reads the toast and dismisses it.
 *
 * Fixes the "two voices talking over each other → frozen screen" bug
 * caused by raw `new Audio()` playing uncoordinated with `useJarvis`.
 */
export default function AgentRemindersToast() {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Poll every 60s
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const r = await api.get("/jarvis/agent_reminders");
        if (stopped) return;
        const rems = r.data?.reminders || [];
        if (rems.length > 0) {
          setQueue(q => {
            const ids = new Set(q.map(x => x.id));
            const next = rems.filter(x => !ids.has(x.id));
            return [...q, ...next];
          });
        }
      } catch {}
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  // Promote next reminder — VISUAL ONLY, no auto-speak.
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(q => q.slice(1));
    }
  }, [current, queue]);

  if (!current) return null;

  const hear = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const tk = localStorage.getItem("rawco_token");
      const tts = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ text: current.text, voice_id: current.voice_id || undefined }),
      });
      if (tts.ok) {
        const blob = await tts.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        audio.onended = () => setPlaying(false);
        audio.onerror = () => setPlaying(false);
        await audio.play().catch(() => setPlaying(false));
      } else {
        setPlaying(false);
      }
    } catch {
      setPlaying(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try { await api.post(`/jarvis/agent_reminders/${current.id}/dismiss`); } catch {}
    setCurrent(null);
    setBusy(false);
  };

  const fix = async () => {
    if (!current.action_key) return;
    setBusy(true);
    try {
      const r = await api.post("/jarvis/swarm_execute", { action_key: current.action_key });
      setCurrent({ ...current, text: r.data?.message || "Done.", action_key: null });
      setTimeout(dismiss, 2400);
    } catch (e) {
      setCurrent({ ...current, text: `Failed: ${e.response?.data?.detail || e.message}`, action_key: null });
      setTimeout(dismiss, 2400);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed bottom-20 left-4 z-[103] rc-panel p-3 w-80"
      data-testid="agent-reminder-toast"
      style={{ background: "rgba(0,4,12,0.96)", border: "1px solid var(--rc-gold)", boxShadow: "0 0 24px rgba(255,184,0,0.3)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="rc-label text-[10px] opacity-80 rc-glow-text">
          // {current.agent?.toUpperCase()} · NUDGE
        </div>
        <button onClick={dismiss} disabled={busy} className="text-[var(--rc-gold)] opacity-60 hover:opacity-100 text-xs" data-testid="agent-reminder-x">✕</button>
      </div>
      <div className="text-[12px] mb-3 whitespace-pre-wrap">{current.text}</div>
      <div className="flex gap-2">
        <button
          onClick={hear}
          disabled={busy || playing}
          className="border border-[var(--rc-gold-dim)] text-[var(--rc-gold)] px-2 py-1.5 text-[10px] tracking-widest opacity-80 hover:opacity-100"
          data-testid="agent-reminder-hear"
          title="Tap to hear this nudge in the agent's voice"
        >
          {playing ? "PLAYING…" : "🔊 HEAR"}
        </button>
        {current.action_key && (
          <button onClick={fix} disabled={busy} className="rc-btn !text-[10px] !py-1.5 flex-1" data-testid="agent-reminder-fix">
            {busy ? "WORKING…" : "✅ YES, FIX IT"}
          </button>
        )}
        <button onClick={dismiss} disabled={busy} className="border border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)] px-3 py-1.5 text-[10px] tracking-widest opacity-70 hover:opacity-100" data-testid="agent-reminder-dismiss">
          DISMISS
        </button>
      </div>
    </div>
  );
}
